import { EventEmitter } from 'events';

/**
 * VolumeManager - Cross-platform volume control with smart detection
 * 
 * Strategy:
 * - Windows: Try WASAPI events first, fallback to smart polling
 * - macOS/Linux: Use smart polling (1-2s intervals)
 * 
 * Features:
 * - Event-based updates (zero CPU when idle on Windows)
 * - Adaptive polling (more frequent when settings are open)
 * - Bidirectional sync (app ↔ system)
 */
class VolumeManager extends EventEmitter {
  constructor() {
    super();
    this.isElectron = typeof window !== 'undefined' && window.electronAPI !== undefined;
    this.platform = null;
    this.currentSpeakerVolume = 100;
    this.useEventBased = false;
    this.pollInterval = null;
    this.isSettingsOpen = false;
    this.isInitialized = false;
    
    // Track last known values to prevent unnecessary events
    this.lastCheck = {
      speaker: 100
    };
  }

  /**
   * Initialize volume manager with platform detection
   */
  async initialize() {
    if (!this.isElectron) {
      console.log('❌ Not running in Electron, volume control disabled');
      return;
    }

    if (this.isInitialized) {
      console.log('⚠️ Volume manager already initialized');
      return;
    }
    
    try {
      // Get platform from Electron
      this.platform = await window.electronAPI.getPlatform();
      console.log(`🖥️ Platform detected: ${this.platform}`);
      
      // Get initial volumes from system
      await this.loadInitialVolumes();
      
      // Use smart polling for all platforms (simple and reliable)
      console.log(`✅ Using smart polling-based volume monitoring`);
      await this.initializeSmartPolling();
      this.isInitialized = true;
      
    } catch (error) {
      console.error('❌ Failed to initialize volume manager:', error);
      // Still try to start polling as last resort
      this.startSmartPolling();
    }
  }

  /**
   * Load initial volume values from system (speaker only)
   */
  async loadInitialVolumes() {
    try {
      const speakerVol = await window.electronAPI.getSpeakerVolume();
      
      this.currentSpeakerVolume = speakerVol;
      this.lastCheck.speaker = speakerVol;
      
      console.log(`🔊 Initial speaker volume: ${speakerVol}%`);
    } catch (error) {
      console.error('Failed to load initial speaker volume:', error);
    }
  }

  /**
   * Initialize smart polling for volume monitoring
   */
  async initializeSmartPolling() {
    console.log('📊 Initializing smart polling...');
    this.startSmartPolling();
  }

  /**
   * Start smart polling with adaptive intervals
   */
  startSmartPolling() {
    // Conservative default: 2 seconds (very lightweight)
    const pollRate = 2000;
    
    this.pollInterval = setInterval(async () => {
      // Only poll if settings are open to save resources
      if (this.isSettingsOpen) {
        await this.updateVolumes();
      }
    }, pollRate);
    
    console.log(`📊 Smart polling started (${pollRate}ms interval, active only when settings open)`);
  }

  /**
   * Check for speaker volume changes and emit events if changed
   */
  async updateVolumes() {
    try {
      // Check speaker volume only
      const speakerVolume = await window.electronAPI.getSpeakerVolume();
      if (Math.abs(speakerVolume - this.lastCheck.speaker) >= 1) { // 1% threshold
        this.lastCheck.speaker = speakerVolume;
        this.currentSpeakerVolume = speakerVolume;
        this.emit('speakerVolumeChanged', speakerVolume);
      }
    } catch (error) {
      console.error('Failed to update speaker volume:', error);
    }
  }

  /**
   * Optimize polling rate based on settings page state
   * More aggressive when settings are open, conservative when closed
   */
  setSettingsOpen(isOpen) {
    this.isSettingsOpen = isOpen;
    
    // Only adjust polling if not using event-based monitoring
    if (!this.useEventBased && this.pollInterval) {
      this.stopPolling();
      
      if (isOpen) {
        // More aggressive polling when settings are active (500ms)
        this.pollInterval = setInterval(() => this.updateVolumes(), 500);
        console.log('⚡ Increased polling rate to 500ms (settings open)');
      } else {
        // Back to conservative polling (2s)
        this.pollInterval = setInterval(() => this.updateVolumes(), 2000);
        console.log('🔋 Reduced polling rate to 2000ms (settings closed)');
      }
    }
    
    // If using event-based, still update immediately when opening settings
    if (this.useEventBased && isOpen) {
      this.updateVolumes();
    }
  }

  /**
   * Set speaker (system output) volume
   */
  async setSpeakerVolume(volume) {
    try {
      await window.electronAPI.setSpeakerVolume(volume);
      this.currentSpeakerVolume = volume;
      this.lastCheck.speaker = volume;
    } catch (error) {
      console.error('Failed to set speaker volume:', error);
      throw error;
    }
  }


  /**
   * Stop polling interval
   */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    console.log('🧹 Cleaning up volume manager...');
    this.stopPolling();
    this.removeAllListeners();
    this.isInitialized = false;
  }

  /**
   * Get current speaker volume
   */
  getSpeakerVolume() {
    return this.currentSpeakerVolume;
  }


  /**
   * Check if volume manager is using event-based monitoring
   */
  isUsingEventBased() {
    return this.useEventBased;
  }

  /**
   * Get current platform
   */
  getPlatform() {
    return this.platform;
  }
}

// Export singleton instance
export const volumeManager = new VolumeManager();
